// @ts-nocheck
import * as React from "react";
import { Logo, Ic, Sidebar, Topbar, LineChart, BarChart, Kpi, useCurrentUser, AppFooter, apiErrorMessage, fmtDuration } from "./common";
import { AlertDetailModal } from "../components/AlertDetailModal";
import { CaseFlowDrilldownModal } from "../components/CaseFlowDrilldownModal";
import { usePermissionsFromSession } from "../components/permissions";
import { NaverMapCanvas } from "../components/naver/NaverMapCanvas";
import { markerStyle } from "../components/naver/markers";
import { attachMarkerTooltip, bindMapTooltipHide } from "../components/naver/tooltip";
// 남양주「N-CARE 통합돌봄 플랫폼 구축 계획(안)」 12단계·★7과제 additive 위젯.
// 서버가 { enabled: false }를 돌려주면 아무것도 렌더하지 않아 R018(김해) 등에는 영향 없음.
import { NamyangjuSupplement } from "../components/namyangju/NamyangjuSupplement";

const SEVERITY_COLOR = { critical: "rose", warning: "amber", info: "sky" };
const SEVERITY_LABEL = { critical: "위험", warning: "경고", info: "알림" };
const STATUS_LABEL = { available: "가용", busy: "바쁨", overloaded: "과부하", moving: "이동중", offline: "오프라인" };
const STATUS_COLOR = { available: "green", busy: "amber", overloaded: "rose", moving: "amber", offline: "gray" };

// Time-window selector. Mirrors the platform dashboard so 권역/플랫폼 UX feel
// the same. Last entry is the default and matches backend DEFAULT_PERIOD.
const PERIODS = [
  { key: "day",     label: "24시간", days: 1 },
  { key: "week",    label: "7일",   days: 7 },
  { key: "month",   label: "30일",  days: 30 },
  { key: "quarter", label: "분기",  days: 90 },
];
const PERIOD_LABEL = Object.fromEntries(PERIODS.map(p => [p.key, p.label]));

function timeAgo(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.max(1, Math.round(ms / 60000));
  if (min < 60) return `${min}분 전`;
  const hours = Math.round(min / 60);
  if (hours < 24) return `${hours}시간 전`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}일 전`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}달 전`;
  return `${Math.round(months / 12)}년 전`;
}

// Local-time today as YYYY-MM-DD. UTC slicing would shift by a day during
// evenings in KST, so build the string from local components instead.
function todayISO() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

// Auto-refresh cadence for live alert delivery. 30s balances backend load
// against time-to-notice for warning/critical alerts arriving while the
// admin is watching the dashboard.
const POLL_INTERVAL_MS = 30_000;
// Toasts only fire for these severities — info alerts are too noisy for
// an interrupt-style notification.
const TOAST_SEVERITIES = new Set(["critical", "warning"]);
// How long a toast stays on screen before auto-dismissing.
const TOAST_AUTO_DISMISS_MS = 6_000;
// localStorage 키 — 데스크톱 알림 옵트인 상태(영구 저장).
const DESKTOP_ALERTS_KEY = "region_dashboard:desktop_alerts";
// 알림음 throttle. 위험 알람이 한꺼번에 도달해도 사운드는 이 간격으로 1회만.
const BEEP_THROTTLE_MS = 5_000;

// 짧은 사인파 beep. 별도 오디오 에셋 없이 Web Audio API로 합성한다.
// AudioContext는 첫 호출 시 lazy 생성 후 재사용 — Chrome의 autoplay 정책상
// 사용자 제스처 이후의 호출에서만 정상 동작한다(옵트인 버튼 클릭이 이를 충족).
function makeBeeper() {
  if (typeof window === "undefined") return () => {};
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return () => {};
  let ctx = null;
  return () => {
    try {
      if (!ctx) ctx = new Ctx();
      if (ctx.state === "suspended") ctx.resume();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.value = 880; // A5
      const t = ctx.currentTime;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.3, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
      osc.start(t);
      osc.stop(t + 0.4);
    } catch { /* silent — sound is best-effort */ }
  };
}
const beep = makeBeeper();

// OS 알림(백그라운드 탭에서만). foreground 탭은 토스트가 더 정보량이 많아
// 중복 알림이 되지 않게 의도적으로 스킵.
function showDesktopNotification(a) {
  if (typeof Notification === "undefined") return;
  if (Notification.permission !== "granted") return;
  if (document.visibilityState === "visible") return;
  try {
    const sev = a.severity === "critical" ? "위험" : "경고";
    const n = new Notification(`[${sev}] ${a.title}`, {
      body: `${a.elderly_name || a.manager_name || ""} · ${a.message}`,
      tag: `region-alert-${a.id}`,
    });
    n.onclick = () => { window.focus(); n.close(); };
  } catch { /* notification API can throw on some platforms */ }
}

function DashboardRegion() {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);
  // 권역 미배정(초대 직후 region_id=null) 전용 상태. 일반 err와 분리해
  // "빈 화면/생경한 에러" 대신 친절한 안내 화면을 띄운다.
  const [noRegion, setNoRegion] = React.useState(false);
  const [period, setPeriod] = React.useState("month");
  // case-flow funnel filter — "all" | "frail" (노쇠 3+) | "critical" (위험)
  const [flowFilter, setFlowFilter] = React.useState("all");
  const [openAlert, setOpenAlert] = React.useState(null);
  // 펑널 단계 드릴다운 모달용 — 클릭한 단계의 status 키.
  const [drilldownStatus, setDrilldownStatus] = React.useState(null);
  const [toasts, setToasts] = React.useState([]);
  // Bump after a successful case create / alert resolve / 30s tick to refresh case_flow + alerts + KPIs.
  const [refreshTick, setRefreshTick] = React.useState(0);
  // Alert ids seen on the previous fetch — used to diff for new arrivals.
  // null means "not yet seeded": on the first successful fetch we record
  // ids without showing toasts (otherwise the initial list would all toast).
  const prevAlertIdsRef = React.useRef(null);
  // 데스크톱 알림 옵트인 — localStorage 영구 저장.
  const [desktopAlerts, setDesktopAlerts] = React.useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(DESKTOP_ALERTS_KEY) === "1";
  });
  // 마지막 알림음 시각 — throttle용.
  const lastBeepAtRef = React.useRef(0);
  const { user, loaded: userLoaded } = useCurrentUser();
  const perms = usePermissionsFromSession({ user, loaded: userLoaded });

  React.useEffect(() => {
    // Honour ?code=Rxxx in the URL so platform-dashboard drilldown works.
    const code = new URLSearchParams(window.location.search).get("code");
    const params = new URLSearchParams();
    if (code) params.set("code", code);
    params.set("period", period);
    params.set("flow_filter", flowFilter);
    fetch(`/api/v1/dashboard/region?${params.toString()}`, { headers: { Accept: "application/json" } })
      // 상태 코드를 먼저 확인한다 — 예전엔 r.json()을 곧장 setData에 넘겨서
      // 에러 응답({error,message})이 데이터로 둔갑하고 이후 data.region.name 접근에서
      // 크래시(빈 화면)가 났다. 401 no_region은 별도 안내 화면으로 분기한다.
      .then(async r => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) { const e = new Error(body.message || `요청 실패 (${r.status})`); (e as any).code = body.error; throw e; }
        return body;
      })
      .then(d => { setData(d); setErr(null); setNoRegion(false); })
      .catch(e => { (e as any).code === "no_region" ? setNoRegion(true) : setErr(apiErrorMessage(e)); });
  }, [period, flowFilter, refreshTick]);

  // 30s polling. Pauses when the tab is hidden (no point refetching for an
  // unseen surface) and immediately refetches the moment the tab returns to
  // foreground so the user sees fresh state on tab-switch back.
  React.useEffect(() => {
    const tick = () => {
      if (document.visibilityState === "visible") setRefreshTick(t => t + 1);
    };
    const id = window.setInterval(tick, POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, []);

  // RegionAlertChannel 구독 — 새 알람이 도달하면 폴링 주기를 기다리지 않고 즉시
  // refetch. 30초 폴링은 cable이 끊겼을 때의 폴백으로 그대로 살아 있다.
  // ActionCable 클라이언트 패키지를 새로 추가하지 않고 raw WebSocket으로 프로토콜만
  // 구현 — subscribe/unsubscribe/ping 처리가 전부이고 표면은 좁다.
  const regionId = data?.region?.id;
  React.useEffect(() => {
    if (!regionId) return;
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${proto}//${window.location.host}/cable`;
    const identifier = JSON.stringify({ channel: "RegionAlertChannel", region_id: regionId });
    let ws;
    try { ws = new WebSocket(url); }
    catch { return; }
    ws.onopen = () => {
      try { ws.send(JSON.stringify({ command: "subscribe", identifier })); }
      catch { /* socket may already be closing */ }
    };
    ws.onmessage = (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch { return; }
      // ActionCable 프로토콜 프레임: welcome / ping / confirm_subscription / reject_subscription
      // 실제 메시지는 { identifier, message } 형태.
      if (msg.type === "ping" || msg.type === "welcome" || msg.type === "confirm_subscription") return;
      if (msg.type === "reject_subscription") return;
      if (msg.identifier !== identifier) return;
      if (msg.message?.type === "alert.new") {
        // 알람 카드 / 토스트 / 사운드 / 데스크톱 알림 모두 기존 diff 경로를 재사용.
        // 여기서는 fetch를 trigger하는 것만 책임.
        setRefreshTick(t => t + 1);
      }
    };
    return () => {
      try {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ command: "unsubscribe", identifier }));
        }
      } catch { /* ignore */ }
      try { ws.close(); } catch { /* ignore */ }
    };
  }, [regionId]);

  // Diff alerts on every successful fetch. First-time-seen ids surface as
  // toasts (critical/warning only). Resolved alerts that drop off the list
  // simply fall out of the snapshot — no toast.
  React.useEffect(() => {
    if (!data?.alerts) return;
    const currentIds = new Set(data.alerts.map(a => a.id));
    if (prevAlertIdsRef.current === null) {
      prevAlertIdsRef.current = currentIds;
      return;
    }
    const prev = prevAlertIdsRef.current;
    const newOnes = data.alerts.filter(a => !prev.has(a.id) && TOAST_SEVERITIES.has(a.severity));
    prevAlertIdsRef.current = currentIds;
    if (!newOnes.length) return;
    let shouldBeep = false;
    newOnes.forEach(a => {
      const tid = `${a.id}-${Date.now()}-${Math.random().toString(36).slice(2,7)}`;
      setToasts(prev => [...prev, { ...a, _tid: tid }]);
      window.setTimeout(() => {
        setToasts(prev => prev.filter(x => x._tid !== tid));
      }, TOAST_AUTO_DISMISS_MS);
      // 위험 알람만 사운드 — 경고는 토스트만으로 충분.
      if (a.severity === "critical") shouldBeep = true;
      // 옵트인 한 경우 OS 알림(백그라운드 탭에서만 표시됨).
      if (desktopAlerts) showDesktopNotification(a);
    });
    // 한 폴링 사이클에 여러 critical이 와도 beep는 throttle 내 1회만.
    if (shouldBeep) {
      const now = Date.now();
      if (now - lastBeepAtRef.current >= BEEP_THROTTLE_MS) {
        lastBeepAtRef.current = now;
        beep();
      }
    }
  }, [data?.alerts, desktopAlerts]);

  // 데스크톱 알림 옵트인 토글. 사용자 제스처 안에서 권한 요청 + AudioContext
  // resume이 함께 일어나도록 같은 핸들러에서 처리.
  const toggleDesktopAlerts = React.useCallback(async () => {
    if (desktopAlerts) {
      window.localStorage.removeItem(DESKTOP_ALERTS_KEY);
      setDesktopAlerts(false);
      return;
    }
    if (typeof Notification === "undefined") {
      window.alert("이 브라우저는 데스크톱 알림을 지원하지 않습니다.");
      return;
    }
    let permission = Notification.permission;
    if (permission === "default") {
      try { permission = await Notification.requestPermission(); } catch { /* legacy */ }
    }
    if (permission !== "granted") {
      window.alert("브라우저 알림 권한이 필요합니다. 브라우저 설정에서 알림을 허용해 주세요.");
      return;
    }
    // 사용자 제스처 안에서 한 번 beep을 울려 AudioContext를 unlock한다
    // (이후의 자동 호출은 사운드 없이도 즉시 동작).
    beep();
    window.localStorage.setItem(DESKTOP_ALERTS_KEY, "1");
    setDesktopAlerts(true);
  }, [desktopAlerts]);

  const dismissToast = (tid) => setToasts(prev => prev.filter(x => x._tid !== tid));

  // 담당 권역 미배정 — 계정은 활성이지만 보여줄 권역이 없는 상태(초대 직후 등).
  // 빈 화면 대신 사유와 다음 행동을 안내한다.
  if (noRegion) return (
    <div className="app" style={{height:'100vh'}}>
      <div className="shell">
        <Sidebar role="region" active="dashboard"/>
        <div style={{display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <Topbar title="권역 운영실" sub="담당 권역 배정 대기"/>
          <div className="main">
            <div style={{maxWidth:540, margin:'12vh auto 0', textAlign:'center'}}>
              <div className="h1" style={{marginBottom:12}}>담당 권역이 아직 배정되지 않았습니다</div>
              <div className="muted" style={{fontSize:14, lineHeight:1.8}}>
                계정은 정상적으로 활성화되었지만, 아직 담당 권역이 지정되지 않아 운영실을 표시할 수 없습니다.<br/>
                시스템 관리자에게 권역 배정을 요청해 주세요. 배정이 완료되면 운영실이 자동으로 표시됩니다.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  if (err)   return <div className="app" style={{padding:40, color:'var(--rose-500)'}}>API 오류: {err}</div>;
  if (!data) return <div className="app muted" style={{padding:40}}>불러오는 중…</div>;

  const r = data.region;
  const k = data.kpis;
  const f = data.case_flow;

  return (
    <div className="app" style={{height:'100vh'}}>
      <div className="shell">
        <Sidebar role="region" active="dashboard"/>
        <div style={{display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <Topbar title={`${r.name} 운영실`} sub={`${r.area} · ${new Date().toLocaleString('ko-KR', {dateStyle:'long', timeStyle:'short'})}`}>
            <a href="/manual/region" title="권역 관리자 사용자 매뉴얼"
               style={{display:'inline-flex', alignItems:'center', gap:6, padding:'8px 12px', borderRadius:9, border:'1px solid var(--border-soft)', background:'var(--surface)', color:'var(--ink-1)', fontSize:12.5, fontWeight:700, textDecoration:'none', whiteSpace:'nowrap'}}>
              <span style={{display:'inline-flex', width:16, height:16, color:'var(--orange-600)'}}>{Ic.help}</span>
              사용자 매뉴얼
            </a>
          </Topbar>
          <div className="main">

            <div style={{display:'flex', alignItems:'flex-end', justifyContent:'space-between', marginBottom:18}}>
              <div>
                <div className="h1">안녕하세요, <span style={{color:'var(--orange-600)'}}>{user?.name || r.manager_name}</span> 관리자님.</div>
                <div className="muted" style={{marginTop:4, fontSize:13}}>
                  오늘 방문 <a href={`/schedule?date=${todayISO()}`}
                                title="오늘 일정으로 이동"
                                style={{color:'var(--ink-1)', fontWeight:700, textDecoration:'underline', textDecorationStyle:'dotted', textUnderlineOffset:3}}>
                                {r.today_visits}건
                             </a>
                  {' · '}
                  위험 케이스 <button type="button"
                                onClick={()=>setFlowFilter('critical')}
                                title="처리 흐름을 위험 케이스만 보기"
                                style={{background:'none', border:'none', padding:0, color:'var(--ink-1)', fontWeight:700, cursor:'pointer', textDecoration:'underline', textDecorationStyle:'dotted', textUnderlineOffset:3, font:'inherit'}}>
                                {r.critical_cases}건
                             </button>
                </div>
                {/* 구조 지표(기관/매니저/예산집행) — 운영 핵심 KPI와 분리해 작은 칩으로 노출. */}
                <div style={{marginTop:6, display:'flex', gap:6, flexWrap:'wrap'}}>
                  <a href="/organizations" className="badge badge-gray" style={{textDecoration:'none', cursor:'pointer'}} title="기관 관리로 이동">
                    기관 <b style={{marginLeft:4}}>{r.org_count}</b>
                  </a>
                  <a href="/managers" className="badge badge-gray" style={{textDecoration:'none', cursor:'pointer'}} title="매니저 관리로 이동">
                    매니저 <b style={{marginLeft:4}}>{r.manager_count}</b>
                  </a>
                  <span className={"badge badge-" + (r.budget_execution >= 90 ? 'rose' : r.budget_execution >= 70 ? 'amber' : 'green')}
                        title="이달 예산 집행률">
                    예산집행 <b style={{marginLeft:4}}>{r.budget_execution}%</b>
                  </span>
                </div>
              </div>
              <div style={{display:'flex', gap:8, alignItems:'center'}}>
                <PeriodSwitch value={period} onChange={setPeriod}/>
                <a className="btn btn-soft btn-md"
                   href={`/api/v1/dashboard/region.csv?code=${encodeURIComponent(r.code)}`}
                   title="권역 운영 스냅샷을 CSV로 내려받습니다 (KPI · 펑널 · 매니저 부하 · 알람)">
                  CSV 내보내기
                </a>
              </div>
            </div>

            <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:18}}>
              <Kpi label="이달 매칭 대상" value={String(k.monthly_matches)} delta={k.delta_matches}  deltaDir={k.delta_matches_dir}  sparkline={k.sparkline_matches}/>
              <Kpi label="매칭 성공률"   value={`${k.success_rate.toFixed(1)}%`} delta={k.delta_success}  deltaDir={k.delta_success_dir}  sparkline={k.sparkline_success}  color="var(--green-500)"/>
              <Kpi label="위험 케이스"   value={String(k.critical_cases)} delta={k.delta_critical} deltaDir={k.delta_critical_dir} sparkline={k.sparkline_critical} color="var(--rose-500)"/>
              <Kpi label="평균 응답시간" value={fmtDuration(k.avg_response_mins)} delta={k.delta_response} deltaDir={k.delta_response_dir} sparkline={k.sparkline_response} color="var(--sky-500)"/>
            </div>

            <div style={{display:'grid', gridTemplateColumns:'1.4fr 1fr', gap:14, marginBottom:14}}>
              <div className="card">
                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 22px 0'}}>
                  <div>
                    <div className="h3">케이스 처리 흐름</div>
                    <div className="muted" style={{fontSize:11, marginTop:2}}>최근 {PERIOD_LABEL[period]} · 단계별 잔여 건수 · <span style={{color:'var(--orange-600)', fontWeight:600}}>단계 클릭 시 케이스 목록</span></div>
                  </div>
                  <div style={{display:'flex', gap:6}}>
                    {[
                      {k:'all',      l:'전체',    c:'gray'},
                      {k:'frail',    l:'노쇠 3+', c:'orange'},
                      {k:'critical', l:'높음',    c:'rose'},
                    ].map(b => {
                      const active = flowFilter === b.k;
                      return (
                        <span key={b.k} onClick={()=>setFlowFilter(b.k)} role="button" aria-pressed={active}
                          className={"badge badge-" + b.c}
                          style={{cursor:'pointer', opacity: active ? 1 : 0.5, fontWeight: active ? 700 : 500}}>
                          {b.l}
                        </span>
                      );
                    })}
                  </div>
                </div>
                <div style={{padding:'18px 22px 22px', display:'flex', alignItems:'flex-end', gap:0}}>
                  {[
                    {l:'보류',  k:'applying',   v:f.applying,   c:'var(--orange-500)'},
                    {l:'매칭',  k:'matching',   v:f.matching,   c:'var(--orange-500)', href:'/cod'},
                    {l:'배정',  k:'assigned',   v:f.assigned,   c:'var(--green-500)'},
                    {l:'케어중', k:'caring',    v:f.caring,     c:'var(--green-600)'},
                    {l:'종료',  k:'closed',     v:f.closed,     c:'var(--ink-4)'},
                  ].map((s,i,arr)=>{
                    const inner = (
                      <>
                        <div style={{fontSize:24, fontWeight:800, color:s.c, fontVariantNumeric:'tabular-nums'}}>{s.v}</div>
                        <div style={{fontSize:11, color:'var(--ink-3)', marginTop:2, fontWeight:600}}>
                          {s.l}{(s.href || s.v > 0) && <span style={{color:'var(--orange-600)', marginLeft:3}}>→</span>}
                        </div>
                        <div style={{height:6, background:s.c, opacity: 0.18 + (s.v/200)*0.8, marginTop:8, borderRadius:99}}/>
                      </>
                    );
                    // 매칭 단계만 풍부한 COD 화면으로 이동 — 나머지 단계는 인라인 모달.
                    // 0건인 단계는 드릴다운해도 빈 리스트만 떠 클릭 어포던스를 비활성화.
                    return (
                      <React.Fragment key={i}>
                        {s.href
                          ? <a href={s.href} className="funnel-stage" style={{flex:1, textAlign:'center', textDecoration:'none', color:'inherit', cursor:'pointer'}} title="AI 자동매칭 (COD) 화면으로 이동">{inner}</a>
                          : s.v > 0
                            ? <button type="button" className="funnel-stage"
                                onClick={()=>setDrilldownStatus(s.k)}
                                title={`${s.l} 단계 케이스 목록 보기`}
                                style={{flex:1, textAlign:'center', background:'none', border:'none', font:'inherit', color:'inherit', cursor:'pointer'}}>
                                {inner}
                              </button>
                            : <div style={{flex:1, textAlign:'center', padding:'6px 4px'}}>{inner}</div>}
                        {i < arr.length - 1 && <div style={{padding:'0 4px', color:'var(--ink-4)', fontWeight:600}}>›</div>}
                      </React.Fragment>
                    );
                  })}
                </div>
                <div style={{borderTop:'1px solid var(--border-soft)', padding:'14px 22px', display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14}}>
                  <div>
                    <div className="muted" style={{fontSize:11, marginBottom:6}}>{PERIOD_LABEL[period]} 신규 케이스</div>
                    {data.weekly_new_cases?.length
                      ? <LineChart data={data.weekly_new_cases} height={50} color="var(--orange-500)" fill="var(--orange-50)"/>
                      : <div className="muted" style={{height:50, display:'grid', placeItems:'center', fontSize:11, background:'var(--bg-sub)', borderRadius:8}}>데이터 없음</div>}
                  </div>
                  <div>
                    <div className="muted" style={{fontSize:11, marginBottom:6}}>{PERIOD_LABEL[period]} 매칭 완료</div>
                    {data.weekly_matches_completed?.length
                      ? <BarChart data={data.weekly_matches_completed} height={50} color="var(--green-500)"/>
                      : <div className="muted" style={{height:50, display:'grid', placeItems:'center', fontSize:11, background:'var(--bg-sub)', borderRadius:8}}>데이터 없음</div>}
                  </div>
                  <div>
                    <div className="muted" style={{fontSize:11, marginBottom:6}}>{PERIOD_LABEL[period]} 방문 일정</div>
                    {data.weekly_visits?.length
                      ? <BarChart data={data.weekly_visits} height={50} color="var(--sky-500)"/>
                      : <div className="muted" style={{height:50, display:'grid', placeItems:'center', fontSize:11, background:'var(--bg-sub)', borderRadius:8}}>데이터 없음</div>}
                  </div>
                </div>
              </div>

              <div className="card">
                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 22px 14px', gap:8}}>
                  <div className="h3">실시간 알람</div>
                  <div style={{display:'flex', alignItems:'center', gap:6}}>
                    <button type="button"
                      onClick={toggleDesktopAlerts}
                      title={desktopAlerts ? "데스크톱 알림 끄기" : "데스크톱 알림 켜기 (백그라운드 탭에서도 위험·경고 알람을 받습니다)"}
                      aria-pressed={desktopAlerts}
                      className={"badge badge-" + (desktopAlerts ? "green" : "gray")}
                      style={{cursor:'pointer', border:'none', font:'inherit'}}>
                      {desktopAlerts ? "알림 ON" : "알림 OFF"}
                    </button>
                    <span className="badge badge-rose">긴급 {data.alerts.filter(a=>a.severity==='critical').length}</span>
                  </div>
                </div>
                {data.alerts.length === 0 && (
                  <div className="muted" style={{padding:'22px', fontSize:12, textAlign:'center'}}>처리 대기 중인 알람이 없습니다.</div>
                )}
                {data.alerts.map((a, i)=>{
                  const c = SEVERITY_COLOR[a.severity];
                  const subjectLabel = a.elderly_name || a.manager_name;
                  return (
                    <div key={a.id} role="button" tabIndex={0}
                      onClick={()=>setOpenAlert(a)}
                      onKeyDown={(e)=>{ if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenAlert(a); } }}
                      style={{display:'flex', alignItems:'flex-start', gap:10, padding:'12px 22px', borderTop: i>0 ? '1px solid var(--border-soft)' : 'none', cursor:'pointer'}}>
                      <span className="dot" style={{background:`var(--${c}-500)`, marginTop:6}}/>
                      <div style={{flex:1, minWidth:0}}>
                        <div style={{fontSize:12.5, fontWeight:600}}>{a.title}</div>
                        <div style={{fontSize:11, color:'var(--ink-3)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                          {subjectLabel && <b style={{color:'var(--ink-2)', marginRight:4}}>{subjectLabel}</b>}
                          {a.message} · {timeAgo(a.created_at)}
                        </div>
                      </div>
                      <span className={"badge badge-" + c}>{SEVERITY_LABEL[a.severity]}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{display:'grid', gridTemplateColumns:'1.4fr 1fr', gap:14}}>
              <div className="card">
                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 22px 12px'}}>
                  <div className="h3">케어매니저 현황</div>
                  <div style={{display:'flex', alignItems:'center', gap:12}}>
                    <span className="muted" style={{fontSize:11}}>가용 {data.managers.filter(m=>m.status==='available').length} / 전체 {data.managers.length}명</span>
                    <a href="/managers"
                      style={{fontSize:11, color:'var(--orange-600)', fontWeight:700, textDecoration:'none'}}>
                      상세 보기 →
                    </a>
                  </div>
                </div>
                <table className="tbl">
                  <thead><tr><th style={{paddingLeft:22}}>매니저</th><th>자격</th><th>소속 기관</th><th>담당 케이스</th><th style={{width:'24%'}}>가용 시간</th><th>상태</th><th/></tr></thead>
                  <tbody>
                  {data.managers.slice(0, 5).map((m,i)=>(
                    <tr key={m.id}>
                      <td style={{paddingLeft:22}}>
                        <div style={{display:'flex', alignItems:'center', gap:10}}>
                          <div className={"avatar sm " + (i%2?'green':'')}>{m.name[0]}</div>
                          <b>{m.name}</b>
                        </div>
                      </td>
                      <td><span className="muted">{m.qualification}</span></td>
                      <td><span className="muted" style={{fontSize:12}}>{m.organization_name || '-'}</span></td>
                      <td className="mono"><b>{m.assigned_cases}</b> <span className="muted">케이스</span></td>
                      <td>
                        <div className={"bar " + (m.load_ratio>0.9?'rose':m.load_ratio>0.7?'':'green')}><span style={{width:`${m.load_ratio*100}%`}}/></div>
                        <div className="muted mono" style={{fontSize:10, marginTop:4}}>{Math.round((1-m.load_ratio)*100)}% 가용</div>
                      </td>
                      <td><span className={"badge badge-" + STATUS_COLOR[m.status]}>{STATUS_LABEL[m.status]}</span></td>
                      <td>
                        {perms.canManageCases && (
                          <button className="btn btn-soft btn-sm"
                            title="이 매니저에게 케이스 배정"
                            onClick={()=>{ window.location.href = `/managers?id=${m.id}&action=assign`; }}>
                            케이스 배정 →
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  </tbody>
                </table>
              </div>

              <div className="card" style={{overflow:'hidden', position:'relative'}}>
                <div style={{padding:'18px 22px 6px', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                  <div className="h3">권역 분포 · {r.area}</div>
                  <a href={`/map?code=${encodeURIComponent(r.code)}`}
                     style={{fontSize:11, color:'var(--orange-600)', fontWeight:700, textDecoration:'none'}}>
                    지도 보기 →
                  </a>
                </div>
                <RegionMiniMap miniMap={data.mini_map}/>
              </div>
            </div>

            {/* 남양주 additive 확장 — 서버가 { enabled: false } 반환 시 아무것도 렌더하지 않음. */}
            <NamyangjuSupplement region={{ id: r.id, code: r.code, name: r.name, area: r.area }}/>

          </div>
          <AppFooter/>
        </div>
      </div>
      {openAlert && (
        <AlertDetailModal
          alert={openAlert}
          onClose={()=>setOpenAlert(null)}
          onResolved={()=>{ setOpenAlert(null); setRefreshTick(t=>t+1); }}
        />
      )}
      {drilldownStatus && (
        <CaseFlowDrilldownModal
          regionId={r.id}
          status={drilldownStatus}
          flowFilter={flowFilter}
          onClose={()=>setDrilldownStatus(null)}
        />
      )}
      {toasts.length > 0 && (
        <div role="region" aria-label="신규 알람" aria-live="polite"
          style={{position:'fixed', top:16, right:16, zIndex:60, display:'flex', flexDirection:'column', gap:8, maxWidth:380}}>
          {toasts.map(t => {
            const c = SEVERITY_COLOR[t.severity] || 'sky';
            return (
              <div key={t._tid} role="button" tabIndex={0}
                onClick={()=>{ dismissToast(t._tid); setOpenAlert(t); }}
                onKeyDown={(e)=>{ if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); dismissToast(t._tid); setOpenAlert(t); } }}
                style={{background:'var(--surface)', borderRadius:10, padding:'12px 14px', boxShadow:'0 6px 20px rgba(0,0,0,0.14)', cursor:'pointer', display:'flex', alignItems:'flex-start', gap:10, borderLeft:`4px solid var(--${c}-500)`, border:'1px solid var(--border-soft)'}}>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontSize:10.5, color:`var(--${c}-500)`, fontWeight:800, letterSpacing:0.2, marginBottom:3}}>
                    새 {SEVERITY_LABEL[t.severity]} 알람
                  </div>
                  <div style={{fontSize:12.5, fontWeight:700}}>{t.title}</div>
                  <div className="muted" style={{fontSize:11, marginTop:2, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden'}}>
                    {(t.elderly_name || t.manager_name) && <b style={{color:'var(--ink-2)', marginRight:4}}>{t.elderly_name || t.manager_name}</b>}
                    {t.message}
                  </div>
                </div>
                <button type="button" aria-label="닫기"
                  onClick={(e)=>{ e.stopPropagation(); dismissToast(t._tid); }}
                  style={{background:'transparent', border:'none', color:'var(--ink-3)', cursor:'pointer', padding:2, alignSelf:'flex-start'}}>
                  {Ic.close}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// 권역 분포 미니맵 — 가짜 SVG 투영을 실제 네이버 지도로 교체한 버전.
//  - 조작 잠금(interactive=false): 대시보드 카드에선 위치 확인만, 본격 줌/팬/검색은
//    헤더의 "지도 보기 →"(/map?code=)로 위임해 풀 지도 화면과 역할이 겹치지 않게.
//  - lazy: IntersectionObserver로 카드가 뷰포트에 들어올 때만 지도 인스턴스를 만들어
//    대시보드 첫 로딩에서 불필요한 타일 요청을 미룬다.
// 마커는 mini_map.points(어르신/매니저)를 네이버 Marker로 직접 그린다. 점이 적어
// (어르신 ≤50 + 매니저 한 자리) 클러스터링 없이 개별 마커로 충분하다.
function RegionMiniMap({ miniMap }) {
  const wrapRef = React.useRef(null);
  const [inView, setInView] = React.useState(false);
  const [map, setMap] = React.useState(null);

  // 카드가 화면(+200px 선행 마진)에 들어오면 1회만 지도를 마운트한다.
  // IntersectionObserver 미지원 환경(구형/테스트)에선 즉시 마운트로 폴백.
  React.useEffect(() => {
    if (inView) return;
    const el = wrapRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") { setInView(true); return; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) { setInView(true); io.disconnect(); }
    }, { rootMargin: "200px" });
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  const center = miniMap?.center;
  const b = miniMap?.bounds;
  // 백엔드 bounds(lat_min/max·lng_min/max) → NaverMapCanvas의 bbox(north/south/east/west).
  const bbox = b ? { north: b.lat_max, south: b.lat_min, east: b.lng_max, west: b.lng_min } : null;

  // points 내용이 실제로 바뀔 때만 마커를 재생성하기 위한 시그니처. 30초 폴링이
  // 동일 내용으로 새 배열 참조를 만들어도 깜빡이지 않게 한다.
  const points = miniMap?.points || [];
  const sig = React.useMemo(
    () => JSON.stringify(points.map(p => [p.kind, p.id, p.lat, p.lng, p.risk_level, p.status])),
    [points]
  );

  // 지도 인스턴스가 잡히면 마커를 그린다. map/points 시그니처 변화마다 전량 재생성
  // (점이 소수라 diff보다 단순 재생성이 더 명확하고 충분히 가볍다).
  React.useEffect(() => {
    if (!map || !window.naver?.maps) return;
    const naver = window.naver;
    // 마커 모양/색/툴팁은 풀 지도(/map)와 같은 단일 출처(markers.ts)를 쓴다 —
    // 예전엔 '어'/'매' 글자 원이라 종류가 색 하나로만 구분됐고 매니저는 상태와
    // 무관하게 늘 초록이었다. compact는 미니맵 크기용 축소 버전.
    const markers = points.map((p) => {
      if (p.lat == null || p.lng == null) return null;
      const style = markerStyle(p.kind === 'elderly' ? 'elderly' : 'manager', p, { compact: true });
      const detailHref = p.id ? (p.kind === 'elderly' ? `/elderly?id=${p.id}` : `/managers?id=${p.id}`) : null;
      const marker = new naver.maps.Marker({
        map,
        position: new naver.maps.LatLng(p.lat, p.lng),
        zIndex: style.zIndex,
        icon: {
          content: style.content,
          anchor: new naver.maps.Point(style.anchorX, style.anchorY),
        },
      });
      attachMarkerTooltip(naver, marker, style.tooltip);
      if (detailHref) {
        naver.maps.Event.addListener(marker, "click", () => { window.location.href = detailHref; });
      }
      return marker;
    }).filter(Boolean);
    const unbindTooltip = bindMapTooltipHide(naver, map);
    return () => {
      unbindTooltip();
      markers.forEach(m => { try { m.setMap(null); } catch {} });
    };
    // sig가 points 내용을 대표하므로 points 참조 자체는 dep에서 제외.
  }, [map, sig]);

  return (
    <div ref={wrapRef}
      style={{height:260, margin:'8px 16px 16px', borderRadius:12, background:'linear-gradient(135deg, #E7E1CF, #D9D0B6)', position:'relative', overflow:'hidden'}}>
      {inView && center
        ? <NaverMapCanvas center={center} bbox={bbox} zoom={14} interactive={false} fitPadding={28} minFitZoom={13} onMapReady={setMap}/>
        : <div style={{position:'absolute', inset:0, display:'grid', placeItems:'center'}}>
            <span className="muted" style={{fontSize:12, fontWeight:600, background:'rgba(255,255,255,0.85)', padding:'6px 12px', borderRadius:8}}>지도 준비 중…</span>
          </div>}
      {/* 범례 — 지도 위 고정 오버레이. pointerEvents 차단으로 마커 클릭을 방해하지 않음. */}
      <div style={{position:'absolute', bottom:10, left:10, zIndex:10, pointerEvents:'none', background:'rgba(255,255,255,0.92)', borderRadius:10, padding:'6px 10px', fontSize:10, display:'flex', gap:10}}>
        <span><span className="dot" style={{background:'var(--orange-500)'}}/> 매니저</span>
        <span><span className="dot" style={{background:'var(--rose-500)'}}/> 위험</span>
        <span><span className="dot" style={{background:'var(--amber-500)'}}/> 주의</span>
        <span><span className="dot" style={{background:'var(--green-500)'}}/> 안정</span>
      </div>
    </div>
  );
}

// Segmented time-window selector. Drives the parent's `period` state so a
// single refetch covers every period-scoped widget.
function PeriodSwitch({ value, onChange }) {
  return (
    <div role="tablist" aria-label="기간 선택"
      style={{display:'inline-flex', background:'var(--bg-sub)', borderRadius:10, padding:3, border:'1px solid var(--border-soft)'}}>
      {PERIODS.map(p => {
        const active = p.key === value;
        return (
          <button key={p.key} role="tab" aria-selected={active} onClick={()=>onChange(p.key)}
            style={{
              padding:'6px 12px', fontSize:12, fontWeight:700,
              border:'none', borderRadius:7, cursor:'pointer',
              background: active ? 'var(--surface)' : 'transparent',
              color:      active ? 'var(--ink-1)'   : 'var(--ink-3)',
              boxShadow:  active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
              transition: 'background 0.15s, color 0.15s',
            }}>
            {p.label}
          </button>
        );
      })}
    </div>
  );
}

export { DashboardRegion };
